import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Menu, X, Loader2, QrCode, Download, Palette, Link2, Users } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { OwnerSidebar } from '@/components/OwnerSidebar';
import { HelpButton } from '@/components/HelpButton';
import { SettingsBrandTab } from '@/pages/settings/SettingsBrandTab';
import { SettingsLinkTab } from '@/pages/settings/SettingsLinkTab';
import { SettingsTeamTab } from '@/pages/settings/SettingsTeamTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRODUCTION_DOMAIN = 'falocomodono.com.br';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72ee028-442b-44ea-ad3d-be1abd62a5cf/artifacts/zdjtc3rc_Logo_Falo_com_o_dono-removebg-preview.png";

const Settings = () => {
  const { user, logout, getToken, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'brand');
  const [settings, setSettings] = useState({
    logo_url: '', primary_color: '#0A2647', secondary_color: '#C5A059',
    thank_you_message: 'Muito obrigado pela sua contribuicao. Vamos trabalhar para melhorar a cada dia.',
    google_invite_message: '', contact_email: '', contact_whatsapp: '',
    notification_emails: [], complaint_tags_enabled: true
  });
  const [profile, setProfile] = useState({ company_name: '', owner_name: '', owner_email: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const qrCodeRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !user && !localStorage.getItem('token')) navigate('/login');
  }, [user, authLoading, navigate]);

  useEffect(() => { if (user) fetchCompany(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCompany = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/company/settings`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setCompany(res.data);
      setSettings(res.data.settings || settings);
      setProfile({ company_name: res.data.name || '', owner_name: res.data.owner_name || '', owner_email: res.data.owner_email || '' });
    } catch { toast.error('Erro ao carregar configuracoes'); }
    finally { setLoading(false); }
  }, [getToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/company/settings`, settings, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Configuracoes salvas!');
      fetchCompany();
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/company/profile`, profile, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Dados atualizados com sucesso!');
      fetchCompany();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao atualizar dados'); }
    finally { setSavingProfile(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const newUrl = response.data.url;
      setSettings(prev => ({ ...prev, logo_url: newUrl }));
      await axios.put(`${API}/company/settings`, { ...settings, logo_url: newUrl }, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Logo salva com sucesso!');
    } catch { toast.error('Erro ao enviar logo'); }
  };

  const getFeedbackUrl = () => `https://${PRODUCTION_DOMAIN}/${company?.slug}`;
  const getPreviewUrl = () => `${window.location.origin}/${company?.slug}`;
  const ownerTitle = settings.owner_title || 'dono';
  const plaqueSubtitle = { dona: 'Use o QR Code e fale direto com a Dona.', neutro: 'Use o QR Code e fale direto com quem manda.' }[ownerTitle] || 'Use o QR Code e fale direto com o Dono.';
  const plaqueHeader = { dona: 'FALE COM A DONA!', neutro: 'FALE COM QUEM MANDA!' }[ownerTitle] || 'FALE COM O DONO!';
  const copyLink = () => {
    const link = getFeedbackUrl();
    navigator.clipboard?.writeText(link).then(() => toast.success('Link copiado!')).catch(() => {
      const ta = document.createElement('textarea'); ta.value = link; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); toast.success('Link copiado!');
    });
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  // ═══════════════ PDF Generation Utils ═══════════════
  const hexToRgb = (hex) => { const h = hex.replace('#', ''); return [parseInt(h.substring(0,2),16), parseInt(h.substring(2,4),16), parseInt(h.substring(4,6),16)]; };
  const luminance = (rgb) => (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;

  const loadFonts = async (pdf) => {
    const toBase64 = (buf) => { const bytes = new Uint8Array(buf); let bin = ''; for (let i=0;i<bytes.length;i++) bin += String.fromCharCode(bytes[i]); return btoa(bin); };
    try {
      const [regRes, boldRes] = await Promise.all([fetch('/fonts/roboto-regular.ttf'), fetch('/fonts/roboto-bold.ttf')]);
      if (!regRes.ok || !boldRes.ok) throw new Error('Font fetch failed');
      const [regBuf, boldBuf] = await Promise.all([regRes.arrayBuffer(), boldRes.arrayBuffer()]);
      pdf.addFileToVFS('Roboto-Regular.ttf', toBase64(regBuf)); pdf.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
      pdf.addFileToVFS('Roboto-Bold.ttf', toBase64(boldBuf)); pdf.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
      return 'Roboto';
    } catch { return 'helvetica'; }
  };

  const loadLogoImage = async () => {
    try {
      const res = await axios.get(`${API}/company/logo-base64`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (res.data.base64) {
        const dataUrl = res.data.base64;
        const format = dataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
        const img = await new Promise((resolve, reject) => { const i = new window.Image(); i.onload = () => resolve(i); i.onerror = () => reject(); i.src = dataUrl; });
        return { dataUrl, width: img.naturalWidth || res.data.width, height: img.naturalHeight || res.data.height, format };
      }
    } catch { /* fallback below */ }
    try {
      const img = await new Promise((resolve, reject) => { const i = new window.Image(); i.crossOrigin = 'anonymous'; i.onload = () => resolve(i); i.onerror = () => reject(); i.src = LOGO_URL; });
      const canvas = document.createElement('canvas'); canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      return { dataUrl: canvas.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight, format: 'PNG' };
    } catch { return null; }
  };

  const getQRCodeImage = async (resolution = 1200) => {
    const feedbackUrl = getPreviewUrl();
    const tempDiv = document.createElement('div'); tempDiv.style.position = 'absolute'; tempDiv.style.left = '-9999px'; document.body.appendChild(tempDiv);
    const { createRoot } = await import('react-dom/client');
    const { QRCodeSVG: TempQR } = await import('qrcode.react');
    const { createElement } = await import('react');
    const root = createRoot(tempDiv);
    root.render(createElement(TempQR, { value: feedbackUrl, size: resolution, level: 'H', includeMargin: true, fgColor: '#000000', bgColor: '#FFFFFF' }));
    await new Promise(r => setTimeout(r, 400));
    const svg = tempDiv.querySelector('svg');
    if (!svg) { root.unmount(); document.body.removeChild(tempDiv); return null; }
    const canvas = document.createElement('canvas'); const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData))); });
    canvas.width = resolution; canvas.height = resolution; ctx.fillStyle = 'white'; ctx.fillRect(0, 0, resolution, resolution); ctx.drawImage(img, 0, 0, resolution, resolution);
    root.unmount(); document.body.removeChild(tempDiv);
    return canvas.toDataURL('image/png');
  };

  const drawCard = (pdf, font, qrImg, logoData, x, y, w, h, companyName) => {
    const s = h / 297; const cx = x + w / 2;
    const c1 = hexToRgb(settings.primary_color || '#000080'); const c2 = hexToRgb(settings.secondary_color || '#FFD700');
    const dark = luminance(c1) <= luminance(c2) ? c1 : c2; const light = luminance(c1) <= luminance(c2) ? c2 : c1;
    pdf.setFillColor(...dark); pdf.rect(x, y, w, h, 'F');
    const topMargin = 20 * s;
    if (logoData?.dataUrl) {
      const maxW = 70*s, maxH = 40*s, natW = logoData.width||1, natH = logoData.height||1, ratio = natW/natH;
      let drawW, drawH; if (ratio >= maxW/maxH) { drawW = maxW; drawH = maxW/ratio; } else { drawH = maxH; drawW = maxH*ratio; }
      try { pdf.addImage(logoData.dataUrl, logoData.format||'JPEG', cx-drawW/2, y+topMargin+(maxH-drawH)/2, drawW, drawH); }
      catch { pdf.setFont(font,'bold'); pdf.setFontSize(Math.max(6,28*s)); pdf.setTextColor(...light); pdf.text(companyName||'', cx, y+topMargin+20*s, {align:'center'}); }
    } else { pdf.setFont(font,'bold'); pdf.setFontSize(Math.max(6,28*s)); pdf.setTextColor(...light); pdf.text(companyName||'', cx, y+topMargin+20*s, {align:'center'}); }
    pdf.setFont(font,'normal'); pdf.setFontSize(Math.max(5,22*s)); pdf.setTextColor(...light); pdf.text('Quer elogiar, criticar ou sugerir uma melhoria?', cx, y+95*s, {align:'center'});
    pdf.setFont(font,'bold'); pdf.setFontSize(Math.max(5,24*s)); pdf.setTextColor(...light); pdf.text(plaqueSubtitle, cx, y+110*s, {align:'center'});
    const qrSz = 85*s, qrPad = 5*s;
    pdf.setFillColor(255,255,255); pdf.roundedRect(cx-qrSz/2-qrPad, y+135*s-qrPad, qrSz+qrPad*2, qrSz+qrPad*2, 4*s, 4*s, 'F');
    pdf.addImage(qrImg, 'PNG', cx-qrSz/2, y+135*s, qrSz, qrSz);
    pdf.setFont(font,'normal'); pdf.setFontSize(Math.max(4,18*s)); pdf.setTextColor(...light); pdf.text('Aponte a camera do seu celular', cx, y+235*s, {align:'center'});
    pdf.setFont(font,'bold'); pdf.setFontSize(Math.max(3,12*s)); pdf.setTextColor(...light); pdf.text('falocomodono.com.br', cx, y+280*s, {align:'center'});
  };

  const downloadGridPDF = async (perSheet) => {
    setGeneratingPdf(true);
    try {
      const companyName = company?.name?.toUpperCase() || 'NOSSA EMPRESA';
      const [qrImg, logoData] = await Promise.all([getQRCodeImage(1200), loadLogoImage()]);
      if (!qrImg) { toast.error('Erro ao gerar QR Code'); return; }
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' }); const font = await loadFonts(pdf);
      const A4W = 210, A4H = 297;
      if (perSheet === 1) { drawCard(pdf, font, qrImg, logoData, 0, 0, A4W, A4H, companyName); }
      else {
        const grids = { 2:{cols:1,rows:2,margin:8,gap:5}, 4:{cols:2,rows:2,margin:6,gap:4}, 8:{cols:2,rows:4,margin:4,gap:3} };
        const { cols, rows, margin, gap } = grids[perSheet]; const RATIO = A4W/A4H;
        const cellW = (A4W-margin*2-gap*(cols-1))/cols, cellH = (A4H-margin*2-gap*(rows-1))/rows;
        let cardW, cardH; if (cellW/cellH > RATIO) { cardH = cellH; cardW = cardH*RATIO; } else { cardW = cellW; cardH = cardW/RATIO; }
        for (let r=0;r<rows;r++) for (let c=0;c<cols;c++) {
          const cellX = margin+c*(cellW+gap), cellY = margin+r*(cellH+gap), cardX = cellX+(cellW-cardW)/2, cardY = cellY+(cellH-cardH)/2;
          pdf.setDrawColor(200,200,200); pdf.setLineWidth(0.15); pdf.setLineDashPattern([1.5,1.5],0); pdf.rect(cardX-0.5, cardY-0.5, cardW+1, cardH+1, 'S'); pdf.setLineDashPattern([],0);
          drawCard(pdf, font, qrImg, logoData, cardX, cardY, cardW, cardH, companyName);
        }
      }
      pdf.save(`placa-${{1:'parede-A4',2:'mesa-A5-2x',4:'4-por-folha',8:'8-por-folha'}[perSheet]}-${company?.slug||'empresa'}.pdf`);
      toast.success('PDF gerado com sucesso!');
    } catch (err) { toast.error('Erro ao gerar PDF: ' + err.message); }
    finally { setGeneratingPdf(false); }
  };

  const downloadBalcaoPDF = async () => {
    setGeneratingPdf(true);
    try {
      const companyName = company?.name?.toUpperCase() || 'NOSSA EMPRESA';
      const [qrImg, logoData] = await Promise.all([getQRCodeImage(1200), loadLogoImage()]);
      if (!qrImg) { toast.error('Erro ao gerar QR Code'); return; }
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [100,150] }); const font = await loadFonts(pdf);
      drawCard(pdf, font, qrImg, logoData, 0, 0, 100, 150, companyName);
      pdf.save(`placa-balcao-10x15cm-${company?.slug||'empresa'}.pdf`); toast.success('Placa de Balcao 10x15cm gerada!');
    } catch (err) { toast.error('Erro ao gerar PDF: ' + err.message); }
    finally { setGeneratingPdf(false); }
  };

  const downloadImplantationLabel = async (heightMm = 80) => {
    setGeneratingPdf(true);
    try {
      const companyName = company?.name || 'Empresa';
      const qrImg = await getQRCodeImage(1200);
      if (!qrImg) { toast.error('Erro ao gerar QR Code'); return; }
      const DPI=300, mm=DPI/25.4, W=Math.round(50*mm), H=Math.round(heightMm*mm), PAD=Math.round(4*mm);
      const canvas = document.createElement('canvas'); canvas.width=W; canvas.height=H; const ctx = canvas.getContext('2d');
      ctx.fillStyle='#FFFFFF'; ctx.fillRect(0,0,W,H); const cx=W/2, contentW=W-PAD*2;
      ctx.fillStyle='#000000'; ctx.textAlign='center'; ctx.textBaseline='top';
      let nameSize=Math.round(4*mm); ctx.font=`bold ${nameSize}px "Helvetica Neue", Arial, sans-serif`;
      while(ctx.measureText(companyName.toUpperCase()).width>contentW&&nameSize>Math.round(1.5*mm)){nameSize-=1;ctx.font=`bold ${nameSize}px "Helvetica Neue", Arial, sans-serif`;}
      const qrSize=heightMm<=60?Math.round(32*mm):Math.round(40*mm), footerSize=Math.round(2*mm);
      const gap1=heightMm<=60?Math.round(2*mm):Math.round(3*mm), gap2=heightMm<=60?Math.round(1.5*mm):Math.round(2.5*mm);
      const totalH=nameSize+gap1+qrSize+gap2+footerSize, startY=Math.round((H-totalH)/2);
      ctx.fillText(companyName.toUpperCase(), cx, startY);
      const qrImage = new Image(); await new Promise((res,rej)=>{qrImage.onload=res;qrImage.onerror=rej;qrImage.src=qrImg;});
      ctx.drawImage(qrImage, cx-qrSize/2, startY+nameSize+gap1, qrSize, qrSize);
      ctx.font=`${footerSize}px "Helvetica Neue", Arial, sans-serif`; ctx.fillStyle='#444444';
      ctx.fillText('falocomodono.com.br', cx, startY+nameSize+gap1+qrSize+gap2);
      const a=document.createElement('a'); a.href=canvas.toDataURL('image/png');
      a.download=`etiqueta_50x${heightMm}mm_${companyName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9]/g,'_').replace(/_+/g,'_')}.png`;
      a.click(); toast.success(`Etiqueta 50x${heightMm}mm baixada!`);
    } catch (err) { toast.error('Erro ao gerar etiqueta: ' + err.message); }
    finally { setGeneratingPdf(false); }
  };

  const downloadQRCode = async () => {
    try { const qrImg = await getQRCodeImage(1200); if (!qrImg) { toast.error('Erro ao gerar QR Code'); return; }
      const a = document.createElement('a'); a.href = qrImg; a.download = `qrcode-${company?.slug||'empresa'}.png`; a.click(); toast.success('QR Code em alta resolucao baixado!');
    } catch { toast.error('Erro ao baixar QR Code'); }
  };

  const downloadAdesivo8x8 = async () => {
    setGeneratingPdf(true);
    try {
      const companyName = company?.name || 'Empresa'; const qrImg = await getQRCodeImage(1200);
      if (!qrImg) { toast.error('Erro ao gerar QR Code'); return; }
      const DPI=300,mm=DPI/25.4,SIZE=Math.round(80*mm),PAD=Math.round(5*mm);
      const canvas=document.createElement('canvas');canvas.width=SIZE;canvas.height=SIZE;const ctx=canvas.getContext('2d');
      ctx.fillStyle='#0A2647';ctx.fillRect(0,0,SIZE,SIZE);
      const borderW=Math.round(1.5*mm);ctx.strokeStyle='#C5A059';ctx.lineWidth=borderW;ctx.strokeRect(borderW/2,borderW/2,SIZE-borderW,SIZE-borderW);
      const cx=SIZE/2,contentW=SIZE-PAD*2,headerSize=Math.round(4*mm);
      ctx.fillStyle='#C5A059';ctx.textAlign='center';ctx.textBaseline='top';ctx.font=`bold ${headerSize}px "Helvetica Neue", Arial, sans-serif`;
      const headerY=PAD+Math.round(1*mm);ctx.fillText(plaqueHeader,cx,headerY);
      let nameSize=Math.round(3*mm);ctx.font=`bold ${nameSize}px "Helvetica Neue", Arial, sans-serif`;ctx.fillStyle='#FFFFFF';
      while(ctx.measureText(companyName.toUpperCase()).width>contentW&&nameSize>Math.round(1.5*mm)){nameSize-=1;ctx.font=`bold ${nameSize}px "Helvetica Neue", Arial, sans-serif`;}
      const nameY=headerY+headerSize+Math.round(2*mm);ctx.fillText(companyName.toUpperCase(),cx,nameY);
      const qrAreaY=nameY+nameSize+Math.round(3*mm),qrSize=Math.round(48*mm),qrPad=Math.round(3*mm);
      ctx.fillStyle='#FFFFFF';ctx.fillRect(cx-qrSize/2-qrPad,qrAreaY,qrSize+qrPad*2,qrSize+qrPad*2);
      const qrImage=new Image();await new Promise((res,rej)=>{qrImage.onload=res;qrImage.onerror=rej;qrImage.src=qrImg;});
      ctx.drawImage(qrImage,cx-qrSize/2,qrAreaY+qrPad,qrSize,qrSize);
      ctx.font=`${Math.round(2*mm)}px "Helvetica Neue", Arial, sans-serif`;ctx.fillStyle='#C5A059';
      ctx.fillText('Aponte a camera e avalie!',cx,qrAreaY+qrSize+qrPad*2+Math.round(2*mm));
      const a=document.createElement('a');a.href=canvas.toDataURL('image/png');
      a.download=`adesivo_8x8cm_${companyName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9]/g,'_').replace(/_+/g,'_')}.png`;
      a.click();toast.success('Adesivo 8x8cm baixado!');
    } catch (err) { toast.error('Erro ao gerar adesivo: ' + err.message); }
    finally { setGeneratingPdf(false); }
  };

  if (authLoading || loading) return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]"><div className="spinner" /></div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="settings-page">
      <div className="lg:hidden bg-[#0A2647] text-white h-16 flex items-center justify-between px-4 sticky top-0 z-40">
        <div className="flex items-center gap-3"><span className="font-semibold text-sm">Falo Com O Dono</span></div>
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white hover:bg-white/10">
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64 bg-[#0A2647]"><OwnerSidebar user={user} mobile setSidebarOpen={setSidebarOpen} handleLogout={handleLogout} activePage="settings" /></div>
        </div>
      )}
      <div className="hidden lg:block w-64 bg-[#0A2647] fixed left-0 top-0 h-screen z-30"><OwnerSidebar user={user} handleLogout={handleLogout} activePage="settings" /></div>

      <div className="lg:ml-64 min-h-screen">
        <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4 sticky top-16 lg:top-0 z-20">
          <h1 className="text-xl font-semibold text-slate-900">Configuracoes</h1>
          <p className="text-sm text-slate-500">Personalize sua experiencia</p>
        </header>

        <main className="p-6 max-w-4xl">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-white border border-slate-200 p-1 rounded-xl w-full">
              <TabsTrigger value="brand" className="flex-1 rounded-lg text-xs sm:text-sm data-[state=active]:bg-[#0A2647] data-[state=active]:text-white"><Palette className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Minha </span>Marca</TabsTrigger>
              <TabsTrigger value="qrcode" className="flex-1 rounded-lg text-xs sm:text-sm data-[state=active]:bg-[#0A2647] data-[state=active]:text-white"><QrCode className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />QR Code</TabsTrigger>
              <TabsTrigger value="link" className="flex-1 rounded-lg text-xs sm:text-sm data-[state=active]:bg-[#0A2647] data-[state=active]:text-white"><Link2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />Link</TabsTrigger>
              <TabsTrigger value="team" className="flex-1 rounded-lg text-xs sm:text-sm data-[state=active]:bg-[#0A2647] data-[state=active]:text-white" data-testid="tab-team"><Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />Equipe</TabsTrigger>
            </TabsList>

            <TabsContent value="brand">
              <SettingsBrandTab settings={settings} setSettings={setSettings} profile={profile} setProfile={setProfile} company={company} saving={saving} savingProfile={savingProfile} handleSave={handleSave} handleSaveProfile={handleSaveProfile} handleLogoUpload={handleLogoUpload} />
            </TabsContent>

            <TabsContent value="qrcode" className="space-y-6">
              <Card className="card-premium">
                <CardHeader className="bg-gradient-to-r from-[#0A2647] to-[#0D3158] text-white rounded-t-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center"><QrCode className="w-5 h-5" /></div>
                    <div><CardTitle className="text-base font-semibold">Materiais de Impressao</CardTitle><CardDescription className="text-slate-300 text-sm">Baixe placas prontas em A4 para o seu balcao</CardDescription></div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row items-center gap-6 mb-8">
                    <div ref={qrCodeRef} className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                      <QRCodeSVG value={getPreviewUrl()} size={160} level="H" includeMargin={true} fgColor={settings.primary_color} bgColor="#FFFFFF" />
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="font-semibold text-slate-900 mb-2">URL da Sua Pagina</h3>
                      <p className="text-sm text-slate-600 mb-1 break-all font-mono bg-slate-50 p-3 rounded-lg border">{getPreviewUrl()}</p>
                      <p className="text-xs text-emerald-600 font-medium mt-1">Este QR Code não expira</p>
                    </div>
                  </div>
                  <div className="border-t border-slate-200 pt-6">
                    <h3 className="font-semibold text-slate-900 mb-1 text-sm">Placas e Etiquetas</h3>
                    <p className="text-xs text-slate-500 mb-5">Escolha o formato ideal para o seu ponto de venda.</p>
                    <div className="mb-5">
                      <p className="text-xs font-semibold text-[#0A2647] uppercase tracking-wider mb-3">Placas em PDF</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                        {[
                          { label: 'Placa de Parede', desc: 'A4 inteiro (210x297mm)', icon: 'A4', action: () => downloadGridPDF(1), testId: 'btn-download-parede-a4' },
                          { label: 'Placas de Mesa', desc: 'A5 — 2 por folha A4', icon: 'A5 2x', action: () => downloadGridPDF(2), testId: 'btn-download-mesa-a5' },
                          { label: '4 por Folha', desc: 'Tamanho medio A4', icon: '4x', action: () => downloadGridPDF(4), testId: 'btn-download-4x' },
                          { label: '8 por Folha', desc: 'Mini adesivos A4', icon: '8x', action: () => downloadGridPDF(8), testId: 'btn-download-8x' },
                          { label: 'Placa de Balcao', desc: '10x15cm — Porta-retrato', icon: '10x15 cm', action: downloadBalcaoPDF, testId: 'btn-download-balcao-10x15', highlight: true },
                        ].map(item => (
                          <div key={item.testId} className={`border rounded-xl p-4 hover:shadow-md transition-all text-center ${item.highlight ? 'border-[#C5A059]/30 bg-[#C5A059]/5 hover:border-[#C5A059]/60' : 'border-slate-200 hover:border-[#C5A059]/40'}`}>
                            <div className="w-10 h-12 bg-[#0A2647] rounded-lg flex items-center justify-center mx-auto mb-2">
                              <span className="text-[#C5A059] font-bold text-[8px] leading-tight">{item.icon}</span>
                            </div>
                            <h4 className="font-semibold text-xs text-slate-900 mb-0.5">{item.label}</h4>
                            <p className="text-[10px] text-slate-500 mb-3">{item.desc}</p>
                            <Button onClick={item.action} disabled={generatingPdf} className={`w-full text-xs h-8 ${item.highlight ? 'bg-[#0A2647] hover:bg-[#0A2647]/90 text-white' : 'btn-secondary'}`} data-testid={item.testId}>
                              {generatingPdf ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Download className="w-3 h-3 mr-1" />PDF</>}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="mb-5">
                      <p className="text-xs font-semibold text-[#0A2647] uppercase tracking-wider mb-3">Etiquetas e Adesivos PNG</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {[
                          { label: 'Adesivo 8x8cm', desc: 'Quadrado com identidade visual', icon: '8x8 cm', action: downloadAdesivo8x8, testId: 'btn-download-adesivo-8x8', highlight: true },
                          { label: 'Etiqueta Padrao', desc: '50x80mm — Impressora termica', icon: '50x80 mm', action: () => downloadImplantationLabel(80), testId: 'btn-download-label-80' },
                          { label: 'Etiqueta Compacta', desc: '50x60mm — Balcao e vitrine', icon: '50x60 mm', action: () => downloadImplantationLabel(60), testId: 'btn-download-label-60' },
                        ].map(item => (
                          <div key={item.testId} className={`border rounded-xl p-4 hover:shadow-md transition-all ${item.highlight ? 'border-[#C5A059]/30 bg-[#C5A059]/5 hover:border-[#C5A059]/60' : 'border-slate-200 hover:border-[#C5A059]/40'}`}>
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 bg-[#0A2647] rounded-lg flex items-center justify-center flex-shrink-0"><span className="text-[#C5A059] font-bold text-[8px] leading-tight">{item.icon}</span></div>
                              <div><h5 className="font-semibold text-xs text-slate-900">{item.label}</h5><p className="text-[10px] text-slate-500">{item.desc}</p></div>
                            </div>
                            <Button onClick={item.action} disabled={generatingPdf} className="w-full bg-[#0A2647] hover:bg-[#0A2647]/90 text-white gap-2 text-xs" data-testid={item.testId}>
                              {generatingPdf ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Download className="w-3 h-3" />Baixar</>}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="border-t border-slate-200 pt-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-1"><h4 className="font-semibold text-sm text-slate-900">Apenas QR Code</h4><p className="text-xs text-slate-500">Imagem PNG em alta resolucao (1200px)</p></div>
                        <Button onClick={downloadQRCode} variant="outline" className="text-xs h-9 gap-1.5" data-testid="btn-download-qrcode"><Download className="w-4 h-4" />Baixar PNG</Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="link">
              <SettingsLinkTab company={company} copyLink={copyLink} getPreviewUrl={getPreviewUrl} />
            </TabsContent>

            <TabsContent value="team">
              <SettingsTeamTab />
            </TabsContent>
          </Tabs>
        </main>
      </div>
      <HelpButton ambiente="dono" tela={`settings_${activeTab}`} />
    </div>
  );
};

export default Settings;
